'use client';

import { useTranslations } from 'next-intl';
import {
  Conditional,
  DrawerBody,
  NoNotification,
  NotificationHeader,
  NotificationSkeletonLoader,
  NotificationDetailError,
  RefreshTooltipButton,
  Drawer,
  FilterTooltipButton,
  useDrawer,
  AccountStatusComponent,
  ClearFilter,
  NotificationKebab,
  NotificationPopover,
  OrderStatusFilter
} from '@cat-ecom/pcc-components';
import { NotificationsContentProps } from '../types';
import { NotificationDetails } from '../notification-details';
import { usePagination } from 'hooks/usePagination';
import {
  useCallback,
  useEffect,
  useState,
  useTransition,
  useMemo
} from 'react';
import { NOTIFICATIONS_DRAWER_PAGE_SIZE } from '../constants';
import { NotificationsFooter } from '../notifications-footer';
import {
  currentOrderStatusFilterType,
  Notification
} from 'services/notifications/types';
import { useNotificationsDrawer } from '../notifications-drawer-context';
import {
  revalidateNotifications,
  updateReadNotifications
} from 'services/notifications/notifications';
import { NotificationsFilterDrawer } from '../notifications-filter-drawer';
import { capitalizeText } from '@cat-ecom/common/utils/stringUtils';
import { REJECTED } from 'services/notifications/constants';
import styles from './notifications-content.module.scss';
import { FilterByAccount } from '../notifications-filter-drawer/filter-by-account';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { NotificationToggle } from '../notification-toggle';
import { NOTIFICATION_PREFERENCES_URL } from 'constants/links';
import { useParams, useRouter } from 'next/navigation';
import { getSeoUrl } from 'utils/links';
import { NotificationDismissBanner } from '../components/notfication-dismiss-banner';
import { FilterByOrderAndPONumberFields } from '../notifications-filter-drawer/filter-by-order-and-po-number-fields';
import { FilterByOrderStatus } from '../notifications-filter-drawer/filter-by-order-status/filter-by-order-status-drawer';
import { NOTIFICATION_ORDER_STATUS_MAP } from '../utils/constants';
import { FeedbackToastBanner } from '../components/notfication-dismiss-banner/feedback-toast-banner';
import {
  getSessionStorage,
  removeSessionStorage
} from '@cat-ecom/common/utils/storageUtils';
import { NotificationStatusChips } from '../components/notification-status-chips';
import {
  FilterChipSignal,
  KeyLabelPair
} from '../components/notification-status-chips/interfaces';
import { STATUS_FAIL, STATUS_SUCCESS } from 'constants/analyticsConstants';
import { fireFormSubmittedEventWithOrderStatus } from './utils';
import { useFormSubmitEvent } from 'hooks/analytics/use-form-submit-event';

export const NotificationsContent = ({
  isIAUser,
  defaultDealer,
  status,
  storeAddress,
  isQuoteEnabled,
  userAttributes,
  isLoading = false,
  calRoles = [],
  notifications = [],
  customerNumbers = [],
  isPCCQuoteNotificationsFilteringFlag
}: NotificationsContentProps) => {
  const t = useTranslations();
  const { fireEvent } = useFormSubmitEvent();
  const formMethods = useForm<FieldValues>({
    defaultValues: {
      filterByAccount: '',
      filterByOrderOrReferenceNumber: '',
      filterByPONumber: '',
      filterByOrderStatuses: [],
      hasPOCharacterError: false,
      hasOrderOrReferenceNumberFieldError: false
    }
  });
  const currentFilterAccount = formMethods.watch('filterByAccount');
  const currentOrderStatusFilter = formMethods.watch('filterByOrderStatuses');
  const currentOrderReferenceNumber = formMethods
    .watch('filterByOrderOrReferenceNumber')
    .toLowerCase();
  const currentFilterPONumber = formMethods
    .watch('filterByPONumber')
    .toLowerCase();
  const hasPOCharacterError = formMethods.watch('hasPOCharacterError');

  const showClearFiltersButton =
    currentFilterAccount ||
    currentOrderStatusFilter.length ||
    currentOrderReferenceNumber ||
    currentFilterPONumber;
  const filterDrawer = useDrawer();
  const filterByAccountDrawer = useDrawer();
  const filterByOrderStatusDrawer = useDrawer();
  const { drawerProps, closeDrawer } = useNotificationsDrawer();

  const [toggleValue, setToggleValue] = useState(() => false);
  const [showDismissAllBanner, setShowDismissAllBanner] = useState(() => false);
  const isDrawerActive = drawerProps.isActive;
  const hasUserSelectedAnyFilter =
    currentFilterAccount !== '' || currentOrderStatusFilter.length !== 0;
  const [accountSelectedForChip, setAccountSelectedForChip] =
    useState<KeyLabelPair>({
      key: '',
      label: ''
    });

  const notificationsTitle = t('NOTIFICATIONS');
  const notificationsEmptyMessage = t('NO_NOTIFICATIONS');
  const tooltipText = t('GO_BUTTON');
  const refreshBtnText = capitalizeText(t('REFRESH'));
  const clearFilterBtnText = t('CLEAR_FILTERS');

  const {
    resultPerPage,
    currentPage,
    paginate,
    onPageChange,
    totalResultsCount
  } = usePagination<Notification>(NOTIFICATIONS_DRAWER_PAGE_SIZE, toggleValue);

  const [isPending, startTransition] = useTransition();

  const notificationsDrawer = useNotificationsDrawer();
  const params = useParams();
  const esiteUrl = getSeoUrl(params);
  const router = useRouter();

  const isError = status === REJECTED;

  const showNoNotificationsBanner = notifications?.length === 0 && !isError;

  const hidePagination =
    isError || totalResultsCount === 0 || isLoading || isPending;
  const trimLeadingZeros = /^0+/;

  //Implementation done in notification-card-header to know if a notification is a quote
  const filterByQuote = useCallback(
    (notification: Notification) =>
      isQuoteEnabled && notification.quoteExpireDays,
    [isQuoteEnabled]
  );

  const filterByOrderReferenceCallback = useCallback(
    (notification: Notification, currentOrderReferenceNumber: string) => {
      const formattedOrderNumber = (
        notification.orderNumber?.startsWith('0')
          ? notification.orderNumber.replace(trimLeadingZeros, '')
          : notification.orderNumber
      )?.toLowerCase();
      const formattedCurrentOrderReferenceNumber =
        currentOrderReferenceNumber?.startsWith('0')
          ? currentOrderReferenceNumber.replace(trimLeadingZeros, '')
          : currentOrderReferenceNumber;
      return (
        notification.referenceId
          .toString()
          .startsWith(currentOrderReferenceNumber) ||
        ((!isQuoteEnabled ||
          (isQuoteEnabled && !filterByQuote(notification))) &&
          formattedOrderNumber?.startsWith(
            formattedCurrentOrderReferenceNumber
          )) ||
        (filterByQuote(notification) &&
          formattedOrderNumber === formattedCurrentOrderReferenceNumber)
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isQuoteEnabled, filterByQuote]
  );

  const filterByAccountCallback = (
    notification: Notification,
    currentAccountFilter: string
  ) => {
    return notification.dcn === currentAccountFilter;
  };

  const filterByOrderStatusCallback = (
    orderStatus: string,
    currentOrderStatusFilter: currentOrderStatusFilterType[]
  ) => {
    return currentOrderStatusFilter.some(
      (orderFilter: currentOrderStatusFilterType) =>
        orderFilter.orderStatus === orderStatus
    );
  };

  const filterNotifications = useCallback(
    (
      notifications: Notification[],
      currentAccountFilter: string,
      currentOrderStatusFilter: currentOrderStatusFilterType[],
      currentOrderReferenceNumber: string,
      currentFilterPONumber: string
    ) => {
      const filteredByAccountNotifications = currentAccountFilter
        ? notifications.filter((item) =>
            filterByAccountCallback(item, currentAccountFilter)
          )
        : notifications;

      const isOrderStatusFilter = currentOrderStatusFilter.length;
      const formStatus = isOrderStatusFilter ? STATUS_SUCCESS : STATUS_FAIL;

      const filteredByOrderStatusNofications = isOrderStatusFilter
        ? filteredByAccountNotifications.filter((item) =>
            filterByOrderStatusCallback(
              NOTIFICATION_ORDER_STATUS_MAP[item.orderStatus],
              currentOrderStatusFilter
            )
          )
        : filteredByAccountNotifications;

      fireFormSubmittedEventWithOrderStatus(
        formMethods.getValues(),
        formStatus,
        fireEvent
      );

      const filteredByOrderReferenceNotifications =
        currentOrderReferenceNumber && currentOrderReferenceNumber.length >= 3
          ? filteredByOrderStatusNofications.filter((item) =>
              filterByOrderReferenceCallback(item, currentOrderReferenceNumber)
            )
          : filteredByOrderStatusNofications;

      const filterPONumberContainsProhibitedCharacters =
        currentFilterPONumber.includes('>') ||
        currentFilterPONumber.includes('<');
      const formattedPONumber = currentFilterPONumber?.startsWith('0')
        ? currentFilterPONumber.replace(trimLeadingZeros, '')
        : currentFilterPONumber;
      const filteredByPONumberNotifications =
        formattedPONumber && !filterPONumberContainsProhibitedCharacters
          ? filteredByOrderReferenceNotifications.filter((notification) =>
              notification.poNumber
                ?.replace(trimLeadingZeros, '')
                .toLowerCase()
                .startsWith(formattedPONumber)
            )
          : filteredByOrderReferenceNotifications;

      return filteredByPONumberNotifications;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterByOrderReferenceCallback, currentFilterPONumber]
  );

  const updateNotificationsToRead = useCallback(async () => {
    const unreadNotifications = resultPerPage.filter(
      (row) => row.isRead === false
    );
    if (unreadNotifications.length > 0) {
      const request = unreadNotifications.map((el) => ({
        notificationId: el.notificationId,
        isRead: true
      }));
      await updateReadNotifications({
        notifications: request
      });
    }
  }, [resultPerPage]);

  const handleDrawerClose = () => {
    const { getValues } = formMethods;
    if (drawerProps.isActive) {
      closeDrawer();
      if (getValues()?.['filterByOrderStatuses']?.length > 0) {
        fireFormSubmittedEventWithOrderStatus(
          getValues(),
          currentOrderStatusFilter?.length > 0 ? STATUS_SUCCESS : STATUS_FAIL,
          fireEvent
        );
      }
      updateNotificationsToRead();
      removeSessionStorage('dismiss-all-feedback');
    }
  };

  const handleRefresh = () => {
    setToggleValue(false);
    startTransition(async () => {
      updateNotificationsToRead();
      await revalidateNotifications();
    });
  };

  const handleClearAllFilters = () => {
    formMethods.setValue('filterByAccount', '');
    formMethods.setValue('filterByOrderStatuses', []);
    formMethods.setValue('filterByOrderOrReferenceNumber', '');
    formMethods.setValue('hasOrderOrReferenceNumberFieldError', false);
    formMethods.setValue('filterByPONumber', '');
    formMethods.setValue('hasPOCharacterError', false);
    setToggleValue(false);
  };

  const handleClearFilter = () => {
    handleClearAllFilters();
    handleRefresh();
  };

  useEffect(() => {
    if (notifications?.length > 0 && !hasPOCharacterError && isDrawerActive) {
      const filteredNotifications = filterNotifications(
        notifications,
        currentFilterAccount,
        currentOrderStatusFilter,
        currentOrderReferenceNumber,
        currentFilterPONumber
      );
      paginate(filteredNotifications);
    }
  }, [
    notifications,
    paginate,
    filterNotifications,
    currentFilterAccount,
    currentOrderStatusFilter,
    currentOrderReferenceNumber,
    currentFilterPONumber,
    hasPOCharacterError,
    isDrawerActive
  ]);

  const handleRedirectNotificationPreferences = () => {
    handleDrawerClose();
    router.push(`${esiteUrl}${NOTIFICATION_PREFERENCES_URL}`);
  };

  const handleStatusChips = (
    signal: FilterChipSignal,
    option: string
  ): void => {
    switch (signal) {
      case FilterChipSignal.Account:
        formMethods.setValue('filterByAccount', '');
        break;

      case FilterChipSignal.OrderStatus: {
        const orderStatusArray = currentOrderStatusFilter?.filter(
          (notificationChip: Notification) =>
            notificationChip.orderStatus !== option
        );
        formMethods.setValue('filterByOrderStatuses', orderStatusArray);
        break;
      }

      default:
        break;
    }
  };

  useEffect(() => {
    setAccountSelectedForChip({
      key: currentFilterAccount.toLowerCase(),
      label: currentFilterAccount
    });
  }, [currentFilterAccount]);

  const customerNumber =
    customerNumbers.length === 1
      ? customerNumbers[0]
      : customerNumbers.find((x) => x.name === currentFilterAccount);
  const selectedAccount: { label: string; value: string } = {
    label: customerNumber?.name || '',
    value: customerNumber?.value || ''
  };

  const dismissAllFeedback = useMemo(
    () => getSessionStorage('dismiss-all-feedback') as 'success' | 'rejected',
    []
  );

  const isActiveNotification = useMemo(
    () => resultPerPage.filter((n: Notification) => !n.isDismissed).length > 0,
    [resultPerPage]
  );

  return (
    <FormProvider {...formMethods}>
      <Drawer
        {...notificationsDrawer.drawerProps}
        data-testid="cat-notification-drawer"
        onBlDrawerClose={handleDrawerClose}
        className={styles['notifications-drawer']}
      >
        <NotificationHeader title={notificationsTitle}>
          {showClearFiltersButton && (
            <ClearFilter
              handleClearFilter={handleClearFilter}
              title={clearFilterBtnText}
              tabIndex={0}
            />
          )}
          <>
            <FilterTooltipButton
              handleEventClose={() => 0}
              isNotificationError={false}
              isNoNotification={false}
              isFilterApplied={false}
              handleFilterDrawer={filterDrawer.initiatorProps}
              tooltipText={tooltipText}
            />
            <NotificationsFilterDrawer
              drawer={filterDrawer}
              notificationsCount={totalResultsCount}
              showClearAllFiltersButton={showClearFiltersButton}
              handleClearAllFilters={handleClearAllFilters}
            >
              <Conditional test={!isIAUser && customerNumbers.length > 0}>
                <AccountStatusComponent
                  accountStatusTitle={t('FOOTER_ACCOUNT')}
                  selectedAccount={selectedAccount}
                  handleClick={filterByAccountDrawer.openDrawer}
                  isDisable={customerNumbers.length === 1}
                />
                <hr />
              </Conditional>
              <OrderStatusFilter
                orderStatusTitle={t('2413_ORDER_SHIPMENT_ORDER_STATUS')}
                handleClick={filterByOrderStatusDrawer.openDrawer}
                orderSelectedList={currentOrderStatusFilter}
                showLineAbove={false}
              />
              <FilterByOrderAndPONumberFields isQuoteEnabled={isQuoteEnabled} />
              <FilterByAccount
                accountsDrawer={filterByAccountDrawer}
                customerNumbers={customerNumbers}
                notificationsCount={totalResultsCount}
                filterDrawer={filterDrawer}
              />
              <FilterByOrderStatus
                orderStatusDrawer={filterByOrderStatusDrawer}
                notificationsCount={totalResultsCount}
                filterDrawer={filterDrawer}
                userAttributes={userAttributes}
                calRoles={calRoles}
                isPCCQuoteNotificationsFilteringFlag={
                  isPCCQuoteNotificationsFilteringFlag
                }
              />
              <NotificationToggle
                checked={toggleValue}
                setToggleValue={setToggleValue}
              />
            </NotificationsFilterDrawer>
          </>
          <RefreshTooltipButton
            title={refreshBtnText}
            handleEventClose={() => null}
            refreshNotification={handleRefresh}
          />
          <NotificationKebab
            title={t('VIEW_MORE_OPTIONS')}
            handleEventClose={() => null}
          >
            <NotificationPopover
              handleRedirect={handleRedirectNotificationPreferences}
              title={t('CAT_NOTIFICATION_PREFERENCES_TITLE')}
              dismissText={t('DISMISS_ALL')}
              isActiveNotification={isActiveNotification}
              handleOpenDismissConfirmationBanner={() =>
                setShowDismissAllBanner(true)
              }
            />
          </NotificationKebab>
        </NotificationHeader>
        <DrawerBody>
          <FeedbackToastBanner variant={dismissAllFeedback} />
          {hasUserSelectedAnyFilter && (
            <NotificationStatusChips
              notificationFilter={{
                account: accountSelectedForChip,
                orderStatus: currentOrderStatusFilter
              }}
              setFilterChip={handleStatusChips}
            />
          )}
          <Conditional
            test={!isLoading && !isPending}
            fallback={<NotificationSkeletonLoader />}
          >
            {showNoNotificationsBanner && (
              <NoNotification
                NO_NOTIFICATIONS={notificationsEmptyMessage}
                className="ms-3 mt-2"
              />
            )}
            <NotificationDismissBanner
              showDismissAllBanner={showDismissAllBanner}
              handleConfirmationBannerCancel={() =>
                setShowDismissAllBanner(false)
              }
              resultPerPage={resultPerPage.filter(
                (n: Notification) => !n.isDismissed
              )}
              handleRefresh={handleRefresh}
            />
            {notifications?.length > 0 && (
              <NotificationDetails
                notificationData={resultPerPage}
                storeAddress={storeAddress}
                isIAUser={isIAUser}
                defaultDealer={defaultDealer}
                handleRefresh={handleRefresh}
                filterDrawer={filterDrawer}
              />
            )}
            {isError && (
              <NotificationDetailError
                NOTIFICATIONS_ERROR={t('NOTIFICATIONS_ERROR')}
              />
            )}
          </Conditional>
        </DrawerBody>
        <NotificationsFooter
          onPageChange={onPageChange}
          currentPage={currentPage}
          totalResultsCount={totalResultsCount}
          hidePagination={hidePagination}
          setToggleValue={setToggleValue}
          toggleValue={toggleValue}
        />
      </Drawer>
    </FormProvider>
  );
};
